import { Component, computed, inject, OnDestroy, OnInit, output, signal } from '@angular/core';
import { KonvaEditorComponent } from '../konva-editor/konva-editor.component';
import { CategoryService } from 'src/app/services/category/category.service';
import { DimensionService } from 'src/app/services/dimension/dimension.service';
import { Category } from 'src/app/types/category';
import { ChartService } from 'src/app/services/chart/chart.service';
import { ChartLimitGateService } from 'src/app/services/chart-limit-gate/chart-limit-gate.service';
import cloneDeep from 'lodash.clonedeep';
import { Dimension } from 'src/app/types/dimension';
import { diff } from 'deep-diff';
import { switchMap } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BaseComponent } from 'src/app/components/_base/base.component';
import { Router } from '@angular/router';
import { Path, RoutePath } from 'src/app/shared/route';
import { TranslatePipe, TranslateService, _ } from '@ngx-translate/core';
import { KonvaWebGLCheckComponent } from '../konva-editor/webgl-check.component';
import { captureException } from '@sentry/angular';
import { LayoutService } from 'src/app/services/layout/layout.service';
import { MatButtonModule } from '@angular/material/button';

@Component({
  templateUrl: './visual-builder.component.html',
  standalone: true,
  selector: 'app-onboarding-visual',
  imports: [KonvaEditorComponent, MatButtonModule, TranslatePipe, KonvaWebGLCheckComponent],
  styles: `
    button[mat-button],
    button[mat-flat-button] {
      --mdc-filled-button-container-height: 36px;
      --mdc-filled-button-label-text-weight: 400;
      --mat-filled-button-horizontal-padding: 12px;
      --mdc-text-button-container-height: 36px;
      --mdc-text-button-label-text-weight: 400;
    }
  `,
})
export class VisualBuilderComponent extends BaseComponent implements OnInit, OnDestroy {
  private readonly categoryService = inject(CategoryService);
  private readonly dimensionService = inject(DimensionService);
  private readonly chartService = inject(ChartService);
  private readonly chartLimitGate = inject(ChartLimitGateService);
  private readonly snackbar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly layoutService = inject(LayoutService);

  readonly settingImagesRoute = RoutePath.getPath(Path['setting/images']).fullPath;
  readonly settingDimensionsRoute = RoutePath.getPath(Path['setting/dimensions']).fullPath;

  readonly back = output<void>();
  readonly continue = output<void>();

  readonly loading = signal(false);
  readonly category = signal<Category | undefined>(undefined);
  readonly baseCategory = signal<Category | undefined>(undefined);

  readonly continueDisabled = computed(() => !this.category());

  ngOnInit() {
    this.onClear();

    this.layoutService.fullscreenMode = true;

    this.sub$ = this.categoryService.selectedCategory$.subscribe((category) => this.category.set(category));
  }

  override ngOnDestroy() {
    this.layoutService.fullscreenMode = false;

    super.ngOnDestroy();
  }

  onContinue() {
    const baseCategory = this.baseCategory();
    const category = this.categoryService.selectedCategory;

    if (!category || !baseCategory) return;

    this.loading.set(true);

    const lhs: Partial<Category> = cloneDeep(baseCategory);
    const rhs: Partial<Category> = cloneDeep(category);

    lhs.dimensions = (lhs.dimensions as Dimension[]).map((d) => d._id) as unknown as Dimension[];
    rhs.dimensions = (rhs.dimensions as Dimension[]).map((d) => d._id) as unknown as Dimension[];

    delete lhs._id;
    delete rhs._id;
    delete lhs.__v;
    delete rhs.__v;

    this.sub$ = this.categoryService
      .updateCategoryAsClient(category._id, diff(lhs, rhs) ?? [])
      .pipe(
        // Onboarding is never interrupted by the plan's published chart limit: past it, the first
        // chart is created as a draft instead, with a notice rather than the upgrade popup.
        switchMap(() =>
          this.chartLimitGate.createPublishedOrDraft('size', (published) =>
            this.chartService.createChart({
              category: category._id,
              published,
              measurementVisibility: category.dimensions.reduce<Record<string, Record<string, boolean>>>(
                (acc, dimension) => {
                  acc[category._id] = acc[category._id] ?? {};

                  acc[category._id][dimension._id] = true;

                  return acc;
                },
                {},
              ),
            }),
          ),
        ),
      )
      .subscribe({
        next: ({ result: chart, keptAsDraft }) => {
          this.loading.set(false);
          this.chartService.chart = chart;
          if (keptAsDraft)
            // A notice, not a dialog: the chart exists as a draft and the merchant can publish it later.
            this.snackbar.open(
              this.translate.instant(_('chart-limit.onboarding-saved-as-draft')),
              this.translate.instant(_('common.close')),
            );
          this.continue.emit();
        },
        error: (error) => {
          captureException(error);
          this.loading.set(false);
          this.snackbar.open(
            this.translate.instant(_('error.error-occured')),
            this.translate.instant(_('common.close')),
            { panelClass: 'snackbar-error' },
          );
        },
      });
  }

  onClear() {
    this.categoryService.selectedCategory = undefined;
    this.dimensionService.categoryDimensions = [];
    this.baseCategory.set(undefined);
  }

  onNavigateToImagePage() {
    this.router.navigate(this.settingImagesRoute);
  }

  onNavigateToManage() {
    this.router.navigate(this.settingDimensionsRoute);
  }

  onCategoryCreated(category: Category) {
    this.baseCategory.set(category);
  }
}
